/**
 * @file index.ts
 *
 * @fileoverview Helper functions for job search analytics events.
 */

import { concatFacetsLabels } from '~/jobiqo/components/Search/facets/utils/facetHelpers';
import {
  DIMENSIONS,
  EventParameters,
  PAGE_TYPES,
  PageType,
  setEventParams,
} from '~/jobiqo/lib/analytics';
import { ANONYMOUS_USER } from '~/jobiqo/lib/auth';
import { Facet } from '~/jobiqo/models/search';
import { User } from '~/jobiqo/models/user';
import { SearchApiJobResult } from '~/jobiqo/types/types';
import { concatArrayItems } from '~/jobiqo/utils/mapper';
import { getCustomJobSearchDimensions } from '~/jobiqo/components/Slots/customJobSearchDimensions';

/**
 * Returns the dimensions for a given job search.
 *
 * @param {User | null} user
 *  The current user.
 * @param {SearchApiJobResult} jobs
 *  Job search API result.
 * @param {string} title
 *  Search title.
 * @param {Facet[]} selectedFacets
 *  Selected facets on the job search page.
 * @param {string} location
 *  Job search location.
 * @param {string} searchQuery
 *  The search query.
 * @returns
 *  The dimensions object for this job.
 */
export const getJobSearchDimensions = (
  user: User | null = null,
  jobs: SearchApiJobResult,
  title: string,
  selectedFacets: Facet[],
  location: string,
  searchQuery?: string,
  pageType: PageType = PAGE_TYPES.JOB_SEARCH,
  organizationName?: string,
): EventParameters =>
  setEventParams({
    [DIMENSIONS.entity_id]: jobs.pages?.map((job) => job?.id ?? '').join(' | '),
    [DIMENSIONS.entity_title]: title,
    [DIMENSIONS.occupations]: concatFacetsLabels(
      selectedFacets.filter(
        (facet) => facet.name === 'field_job_occupational_field_parents_all',
      ),
    ),
    [DIMENSIONS.employment_types]: concatFacetsLabels(
      selectedFacets.filter(
        (facet) => facet.name === 'field_job_employment_type',
      ),
    ),
    [DIMENSIONS.regions]: location,
    [DIMENSIONS.current_user_id]: user?.id,
    [DIMENSIONS.current_user_role]:
      concatArrayItems(user?.roles, 'targetId') ?? ANONYMOUS_USER,
    [DIMENSIONS.companies]: concatFacetsLabels(
      selectedFacets.filter((facet) => facet.name === 'job_organization_name'),
    ),
    [DIMENSIONS.organization_name]: organizationName,
    [DIMENSIONS.search_query]: searchQuery,
    [DIMENSIONS.page_type]: pageType,
    ...getCustomJobSearchDimensions(
      user,
      jobs,
      title,
      selectedFacets,
      location,
      searchQuery,
    ),
  });
